import React from 'react';
import { View, Text, StyleSheet } from '@react-pdf/renderer';
import {
  pdfColors, pdfFontSize, pdfLetter, pdfPageFrame, pdfSpacing,
} from '../theme';
import type { SchoolBranding, DocumentMeta } from '../types';
import { fmtDate } from '../format';
import LogoMark from './LogoMark';

interface Props {
  branding: SchoolBranding;
  meta: DocumentMeta;
}

const styles = StyleSheet.create({
  // ── Masthead (first page only, in flow) ────────────────────────────────
  masthead: {
    // The page reserves `contentTop` for the running header on pages 2+.
    // Page 1 has no running header, so the masthead pulls up into that band.
    marginTop: pdfPageFrame.mastheadTop - pdfPageFrame.contentTop,
    marginBottom: pdfSpacing.lg,
  },
  mastRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingBottom: 12,
  },
  logo: { marginRight: pdfSpacing.md },
  idBlock: { flex: 1, paddingRight: pdfSpacing.md },
  schoolName: {
    fontSize: pdfFontSize.xxl,
    fontWeight: 700,
    color: pdfColors.textPrimary,
    letterSpacing: pdfLetter.tight,
    lineHeight: 1.15,
  },
  tagline: {
    fontSize: pdfFontSize.xs,
    color: pdfColors.textMuted,
    marginTop: 2,
  },
  rightBlock: {
    alignItems: 'flex-end',
    maxWidth: '46%',
  },
  titleLabel: {
    fontSize: pdfFontSize.xs,
    fontWeight: 700,
    color: pdfColors.brand,
    textTransform: 'uppercase',
    letterSpacing: pdfLetter.openStrong,
  },
  subtitle: {
    fontSize: pdfFontSize.lg,
    color: pdfColors.textPrimary,
    fontWeight: 600,
    marginTop: 3,
    textAlign: 'right',
  },
  date: {
    fontSize: pdfFontSize.xxs,
    color: pdfColors.textMuted,
    marginTop: 3,
  },
  // Two-weight rule: a strong ink line with a short brand accent riding on
  // it — the "printed letterhead" signature without a coloured banner.
  rule: {
    height: 1.2,
    backgroundColor: pdfColors.textPrimary,
  },
  ruleAccent: {
    position: 'absolute',
    left: 0,
    top: -1,
    width: 56,
    height: 3.2,
    backgroundColor: pdfColors.brand,
  },

  // ── Running header (pages 2+, fixed) ───────────────────────────────────
  runningSlot: {
    position: 'absolute',
    top: pdfPageFrame.runningHeaderTop,
    left: pdfSpacing.pageX,
    right: pdfSpacing.pageX,
  },
  running: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingBottom: 5,
    borderBottomWidth: 0.5,
    borderBottomColor: pdfColors.divider,
    borderBottomStyle: 'solid',
  },
  runningLeft: {
    flex: 1,
    fontSize: pdfFontSize.xxs,
    fontWeight: 600,
    color: pdfColors.textSecondary,
  },
  runningRight: {
    fontSize: pdfFontSize.xxs,
    color: pdfColors.textMuted,
  },
});

/** First-page letterhead: logo + school identity on the left, document title,
 *  subtitle and issue date on the right, closed by the two-weight rule. */
export const Masthead: React.FC<Props> = ({ branding, meta }) => (
  <View style={styles.masthead} wrap={false}>
    <View style={styles.mastRow}>
      <View style={styles.logo}>
        <LogoMark branding={branding} size={46} />
      </View>
      <View style={styles.idBlock}>
        <Text style={styles.schoolName}>{branding.name}</Text>
        {branding.tagline && <Text style={styles.tagline}>{branding.tagline}</Text>}
      </View>
      <View style={styles.rightBlock}>
        <Text style={styles.titleLabel}>{meta.title}</Text>
        {meta.subtitle && <Text style={styles.subtitle}>{meta.subtitle}</Text>}
        {meta.generatedAt && (
          <Text style={styles.date}>Issued {fmtDate(meta.generatedAt)}</Text>
        )}
      </View>
    </View>
    <View style={styles.rule}>
      <View style={styles.ruleAccent} />
    </View>
  </View>
);

/** Slim header repeated on every page after the first, so a loose page 3 of
 *  a merit list still says which school and document it belongs to. Counted
 *  within its own <Page>, so in a document of many (bulk transcripts) each
 *  one opens on its letterhead, not under a running header. */
export const RunningHeader: React.FC<Props> = ({ branding, meta }) => (
  <View
    fixed
    style={styles.runningSlot}
    render={({ subPageNumber }) =>
      subPageNumber > 1 ? (
        <View style={styles.running}>
          <Text style={styles.runningLeft}>
            {branding.name}  ·  {meta.title}
          </Text>
          {meta.subtitle && <Text style={styles.runningRight}>{meta.subtitle}</Text>}
        </View>
      ) : null
    }
  />
);

export default Masthead;
